<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { graphql } from '@/gql';
import { useRouteQuery } from '@vueuse/router';
import { useApolloClient } from '@vue/apollo-composable';
import { format, parseISO } from 'date-fns';
import { computed, ref } from 'vue';
import confetti from 'canvas-confetti';
import { VIcon, VTooltip, VBtn } from 'vuetify/components';
import { RouterLink } from 'vue-router';
import { RouteName } from '@/router';
import type { CreateDeliverableInput, Deliverable } from '@/gql/graphql';
import DeliverableStatusMenu from './DeliverableStatusMenu.vue';
import DeliverableTypeSymbol from '@/components/DeliverableTypeSymbol.vue';
import DeliverableDeleteDialog from './DeliverableDeleteDialog.vue';
import BottleneckCreateModal from './BottleneckCreateModal.vue';
import EscalationCreateModal from './EscalationCreateModal.vue';
import DeliverableFinishDialog from './DeliverableFinishDialog.vue';
import DeliverableFinishBottlenecksModal from './DeliverableFinishBottlenecksModal.vue';
import type { DeliverableTableSubItem } from './DeliverableTable.vue';
import {
    DeliverableStatus as GqlDeliverableStatus,
    DeliverableState,
} from '@/gql/graphql';
import { useMutation } from '@vue/apollo-composable';
import { useNotificationStore } from '@/stores/notification';
import { FormName, useFormSession } from '@/composables/form-session';
import DeliverableUnlinkDialog from './DeliverableUnlinkDialog.vue';

type DeliverableStatus =
    | 'finished'
    | 'escalation'
    | 'pending'
    | 'bottleneck'
    | 'normal';

const { t } = useI18n({
    messages: {
        nl: {
            copy: 'Kopie',
            delete: 'Verwijderen',
            duplicate: 'Dupliceren',
            bottleneck: 'Bottleneck',
            finished: 'Afgerond',
            escalation: 'Escalatie',
            pending: 'Te voltooien',
            duplicate_success: '@:deliverable is gedupliceerd',
            duplicate_error:
                'Er is een fout opgetreden bij het dupliceren van de @.lower:deliverable',
            finish_success: '@:deliverable is afgerond',
            finish_error:
                'Er is een fout opgetreden bij het afronden van het @.lower:deliverable',
            pending_success: '@:deliverable is geupdatet',
            pending_error:
                'Er is een fout opgetreden bij het updaten van de @.lower:deliverable',
        },
        en: {
            copy: 'Copy',
            delete: 'Delete',
            duplicate: 'Duplicate',
            bottleneck: 'Bottleneck',
            finished: 'Finished',
            escalation: 'Escalation',
            pending: 'Pending',
            duplicate_success: '@:deliverable has been duplicated',
            duplicate_error:
                'An error occurred while duplicating the @.lower:deliverable',
            finish_success: '@:deliverable has been finished',
            finish_error:
                'An error occurred while finishing the @.lower:deliverable',
            pending_success: '@:deliverable has been set to pending',
            pending_error:
                'An error occurred while pending the @.lower:deliverable',
        },
    },
});

const notificationStore = useNotificationStore();
const { resolveClient } = useApolloClient();
const formSession = useFormSession();

const props = withDefaults(
    defineProps<{
        currentProjectId: string;
        item: DeliverableTableSubItem;
        canUpdate: boolean;
        canEscalate: boolean;
        subitem?: boolean;
    }>(),
    { subitem: false }
);
const stateLabel = ref<DeliverableStatus | DeliverableState | undefined>(
    undefined
);

const templateState = computed<DeliverableStatus | DeliverableState>(() => {
    return (stateLabel.value ?? props.item.state === 'escalation')
        ? 'escalation'
        : props.item.type.status;
});

const {
    mutate: updateDeliverableStatus,
    loading: updateDeliverableStatusLoading,
} = useMutation(
    graphql(`
        mutation UpdateDeliverableStatus(
            $input: UpdateDeliverableStatusInput!
        ) {
            updateDeliverableStatus(input: $input) {
                ok
            }
        }
    `),
    {
        refetchQueries: ['ProjectPhases', 'Deliverable', 'MetroProjects'],
        awaitRefetchQueries: true,
    }
);

const { mutate: createDeliverable, loading: isCreating } = useMutation(
    graphql(`
        mutation CreateDeliverable($input: CreateDeliverableInput!) {
            createDeliverable(input: $input) {
                deliverable {
                    id
                }
            }
        }
    `),
    {
        refetchQueries: ['ProjectPhases'],
        awaitRefetchQueries: true,
    }
);
async function handleDuplicateDeliverable() {
    const client = resolveClient();

    try {
        const response = await client.query<{
            loading: boolean;
            deliverable: Pick<
                Deliverable,
                'name' | 'description' | 'milestone' | 'projectId' | 'phaseId'
            >;
        }>({
            query: graphql(`
                query DeliverableDuplicateInfo($deliverableId: ID!) {
                    deliverable(deliverableId: $deliverableId) {
                        name
                        description
                        milestone
                        projectId
                        phaseId
                    }
                }
            `),
            variables: {
                deliverableId: props.item.id,
            },
        });

        const sourceDeliverable = response.data.deliverable;

        const input: CreateDeliverableInput = {
            name: `${t('copy')} ${sourceDeliverable.name}`,
            description: sourceDeliverable.description,
            ...(props.item.startDate && { startDate: props.item.startDate }),
            endDate: props.item.endDate,
            milestone: sourceDeliverable.milestone,
            projectId: sourceDeliverable.projectId,
            phaseId: sourceDeliverable.phaseId,
        };

        await createDeliverable({
            input,
        });

        notificationStore.add({
            type: 'success',
            message: t('duplicate_success'),
        });
    } catch {
        notificationStore.add({
            type: 'error',
            message: t('duplicate_error'),
        });
    }
}

async function handleStatusMutation(status: GqlDeliverableStatus) {
    const msg = status === GqlDeliverableStatus.FINISHED ? 'finish' : 'pending';
    try {
        await updateDeliverableStatus({
            input: {
                deliverableId: props.item.id,
                status,
            },
        });

        if (status === GqlDeliverableStatus.FINISHED) {
            confetti();
        }

        notificationStore.add({
            type: 'success',
            message: t(`${msg}_success`),
        });
    } catch {
        notificationStore.add({
            type: 'error',
            message: t(`${msg}_error`),
        });
    }
}

async function handleStatusSelect(newStatus: DeliverableStatus) {
    const hasBottlenecksAndOrEscalations = [
        'bottleneck',
        'escalation',
    ].includes(templateState.value);

    if (newStatus === templateState.value) return;

    switch (newStatus) {
        case 'pending':
            /*
                If there are bottlenecks/escalations the user needs to resolve them in the bottlenecks modal.
                If there aren't, change deliverable status to pending.
            */
            if (hasBottlenecksAndOrEscalations) {
                showDeliverableBottlenecksModal();
            } else if (templateState.value === 'finished') {
                handleStatusMutation(GqlDeliverableStatus.PENDING);
            }
            return;
        case 'bottleneck':
            /*
                If there are bottlenecks/escalations, the user has to change status in the bottlenecks modal
                If there are none yet, show the create bottleneck form
            */
            if (hasBottlenecksAndOrEscalations) {
                showDeliverableBottlenecksModal();
            } else {
                showBottleneckCreateModal();
            }
            return;
        case 'escalation':
            /*
                If there are bottlenecks/escalations, the user has to change status in the bottlenecks modal
                If there are none yet, show the create escalation form
            */
            if (!hasBottlenecksAndOrEscalations) {
                showEscalationCreateModal();
            } else {
                showDeliverableBottlenecksModal();
            }
            return;
        case 'finished':
            /*
                If there are bottlenecks/escalations,the user needs to resolve them in the bottlenecks modal
                If there are none, finish the deliverable
            */
            if (hasBottlenecksAndOrEscalations) {
                showDeliverableBottlenecksModal();
            } else if (templateState.value === 'pending') {
                handleStatusMutation(GqlDeliverableStatus.FINISHED);
            }
            return;
        default:
            console.warn('Unhandled status:', newStatus);
    }
}

const isShowingEscalationCreateModal = ref(false);
const isShowingBottleneckCreateModal = ref(false);
const isShowingDeliverableFinishDialog = ref(false);
const isShowingDeliverableBottlenecksModal = ref(false);
const canUpdateDeliverable = computed(() => {
    return (
        props.canUpdate &&
        (props.item.state !== DeliverableState.ESCALATION || props.canEscalate)
    );
});

function showEscalationCreateModal() {
    isShowingEscalationCreateModal.value = true;
}

function showBottleneckCreateModal() {
    isShowingBottleneckCreateModal.value = true;
}
function showDeliverableBottlenecksModal() {
    isShowingDeliverableBottlenecksModal.value = true;
}

const formQuery = useRouteQuery('form');
const formIdQuery = useRouteQuery('deliverableId');

formSession.onResult(() => {
    if (!formIdQuery.value || formIdQuery.value !== props.item.id) return;
    if (!formSession.session.value?.fields) return;

    if (formQuery.value && formIdQuery.value) {
        switch (formQuery.value) {
            case FormName.escalation_create:
                if (isShowingEscalationCreateModal.value) return;
                isShowingEscalationCreateModal.value = true;
                break;
            case FormName.bottleneck_create:
                if (isShowingBottleneckCreateModal.value) return;
                isShowingBottleneckCreateModal.value = true;
                break;
        }
    }
});

const isLinkedEntry = computed(() => {
    return props.item.linkedProjectId === props.currentProjectId;
});
</script>

<template>
    <EscalationCreateModal
        v-model="isShowingEscalationCreateModal"
        :deliverable-id="item.id"
    />
    <BottleneckCreateModal
        v-model="isShowingBottleneckCreateModal"
        :deliverable-id="item.id"
    />
    <DeliverableFinishBottlenecksModal
        v-model="isShowingDeliverableBottlenecksModal"
        :deliverable-id="item.id"
        :loading="false"
        :can-update
        :can-delete="canUpdate"
        :can-escalate
        @all-solved="handleStatusMutation(GqlDeliverableStatus.FINISHED)"
    />
    <DeliverableFinishDialog
        v-model="isShowingDeliverableFinishDialog"
        :deliverable-id="item.id"
        :refetch-queries="['Deliverable', 'ProjectPhases', 'MetroProjects']"
    />
    <tr
        class="v-data-table__tr"
        :class="subitem ? 'v-data-table__tr--expansion' : null"
    >
        <td class="v-data-table__td"></td>
        <td class="v-data-table__td">
            <router-link
                variant="text"
                :to="{
                    name: RouteName['dashboard.deliverable'],
                    params: { id: item.id },
                }"
                class="text-primary text-decoration-none font-weight-bold"
            >
                <VTooltip
                    v-if="isLinkedEntry"
                    interactive
                    location="end center"
                >
                    <template #activator="{ props: activatorProps }">
                        <span v-bind="activatorProps">
                            <VIcon size="small" class="mr-1" icon="$link" />
                            {{ item.name }}
                        </span>
                    </template>

                    <RouterLink
                        :to="{
                            name: RouteName['dashboard.project'],
                            params: { id: item.project.id },
                        }"
                    >
                        <VBtn size="small" variant="text" prepend-icon="$link">
                            {{ item.project.name }}
                        </VBtn>
                    </RouterLink>
                </VTooltip>
                <span v-else>{{ item.name }}</span>
            </router-link>
        </td>
        <td class="v-data-table__td">
            <DeliverableTypeSymbol
                :milestone="item.type.isMilestone"
                :status="item.type.status"
                :state="item.state"
            />
        </td>
        <td class="v-data-table__td">
            <DeliverableStatusMenu
                v-if="canUpdateDeliverable"
                size="small"
                :project="item.project"
                :label="templateState"
                :current-status="templateState"
                :loading="updateDeliverableStatusLoading"
                @select="handleStatusSelect"
            />
            <span
                v-else
                class="text-label-large text-none px-0 text-neutral-40"
            >
                {{ t(templateState) }}
            </span>
        </td>
        <td class="v-data-table__td">
            <template v-if="item.startDate">
                {{ format(parseISO(item.startDate), 'd-M-yyyy') }}
            </template>
        </td>
        <td class="v-data-table__td">
            <template v-if="item.endDate">
                {{ format(parseISO(item.endDate), 'd-M-yyyy') }}
            </template>
        </td>
        <td class="v-data-table__td pl-2">
            <div class="d-flex justify-end">
                <v-btn-icon
                    v-if="!isLinkedEntry"
                    icon="$copy"
                    size="x-small"
                    :aria-label="t('duplicate')"
                    :loading="isCreating"
                    @click="handleDuplicateDeliverable"
                ></v-btn-icon>

                <template v-if="canUpdateDeliverable">
                    <DeliverableUnlinkDialog
                        v-if="
                            item.linkedProjectId &&
                            item.linkedProjectId === currentProjectId
                        "
                        :project-id="item.linkedProjectId"
                        :deliverable-id="item.id"
                    >
                        <template
                            #activator="{ props: templateProps, loading }"
                        >
                            <v-btn-icon
                                size="x-small"
                                icon="$unlink"
                                :loading
                                v-bind="templateProps"
                            ></v-btn-icon>
                        </template>
                    </DeliverableUnlinkDialog>

                    <DeliverableDeleteDialog v-else :deliverable-id="item.id">
                        <template
                            #activator="{ props: templateProps, loading }"
                        >
                            <v-btn-icon
                                size="x-small"
                                icon="$delete"
                                :aria-label="t('delete')"
                                :loading
                                v-bind="templateProps"
                            />
                        </template>
                    </DeliverableDeleteDialog>
                </template>
            </div>
        </td>
    </tr>
</template>
